import rawAxios from "axios";
import axios, { baseUrl } from "../axios/axios";
import { CnCOrderResponse } from "../types/api";
import {
  ConsultCnCOrderRequest,
  ConsultOrderPrefillResponse,
  CncOrderDeliveryType,
  DeliveryTimeSlotsResponse,
  BranchServiceItem,
  ItemInMarketplaceCountResponse,
} from "../types/consultOrder.types";
import { STANDARD_DELIVERY_DAYS } from "../utils/consultOrder.constants";

// ITAM-2583 consult-order service.
//
// prefill (GET) and create (POST) go through the shared, pre-configured axios
// instance (bearer + baseURL). Their meaningful error statuses (404 not-ready,
// 400 validation, 500 business-rule) do NOT trip the global 401/403 -> logout
// interceptor, so the shared instance is correct for them.
//
// The two delivery PRE-CHECK endpoints, however, currently return 403 for the
// consult actor's role (PRODAJNO_MESTO_USER — verified VC-2 gap). Routed through
// the shared instance, that EXPECTED 403 would fire the global logout interceptor
// and drop the pharmacist's whole session mid-order. So the pre-checks use a bare
// axios call (baseURL + bearer added manually) that is NOT wrapped by the logout
// interceptor; a 403/insufficient-role simply rejects and the DeliverySection
// handles it as "zone/slots unavailable" without logging the user out.

const bearerHeader = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * GET prefill. Returns the payload on 200, or `null` on 404 (a normal
 * "not both joined yet / consult ended" state — NOT an error). Any other status
 * rejects.
 */
export const getConsultPrefill = (
  consultantId: number,
  signal?: AbortSignal
): Promise<ConsultOrderPrefillResponse | null> => {
  return axios
    .get<ConsultOrderPrefillResponse>(
      `/click_and_collect_order/online_consult/prefill/consultant_id/${consultantId}`,
      { signal }
    )
    .then((res) => res.data)
    .catch((error) => {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    });
};

/**
 * POST create. Returns the full CnCOrderResponse (201 body). The browser sends
 * its own User-Agent automatically; we never set that header from JS.
 */
export const createConsultOrder = (
  body: ConsultCnCOrderRequest,
  signal?: AbortSignal
): Promise<CnCOrderResponse> => {
  return axios
    .post<CnCOrderResponse>("/click_and_collect_order/online_consult", body, {
      signal,
    })
    .then((res) => res.data);
};

/**
 * GET the branch SERVICE items (delivery-fee items) for the pharmacist's own
 * branch. Uses the NON-intercepted (bare) axios exactly like the two delivery
 * pre-checks: this endpoint is guarded by ADMIN/APOTEKA_USER, so if the consult
 * actor's role is ever insufficient the 403 must degrade to a "fee unavailable"
 * state — it must NOT trip the global 401/403 -> logout interceptor and drop the
 * pharmacist's session mid-order (see [[axios-interceptor-global-logout]]).
 *
 * `numOfReturns` is sent large so the two fee items ("Service fee standard/fast")
 * can never be paginated off the first page (the backend default page size is
 * only 16). An empty branch returns 404 (backend convention) -> resolves to `[]`.
 */
export const getBranchServiceItems = (
  marketplaceId: number,
  branchMarketplaceId: number,
  signal?: AbortSignal
): Promise<BranchServiceItem[]> => {
  return rawAxios
    .get<ItemInMarketplaceCountResponse>(
      `${baseUrl}/item_in_marketplace/items/${branchMarketplaceId}/${marketplaceId}/0`,
      {
        params: { filter: "item.type:'SERVICE'", numOfReturns: 1000 },
        headers: bearerHeader(),
        signal,
      }
    )
    .then((res) => res.data?.list ?? [])
    .catch((error) => {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    });
};

export interface DeliveryDistanceParams {
  lat: number;
  lng: number;
  marketplaceLat: number;
  marketplaceLng: number;
  deliveryType: CncOrderDeliveryType;
}

/**
 * GET check_delivery_distance -> boolean (address within zone for the type).
 * Uses a NON-intercepted axios so an expected VC-2 403 cannot log the user out.
 */
export const checkDeliveryDistance = (
  params: DeliveryDistanceParams,
  signal?: AbortSignal
): Promise<boolean> => {
  const { lat, lng, marketplaceLat, marketplaceLng, deliveryType } = params;
  return rawAxios
    .get<boolean>(
      `${baseUrl}/click_and_collect_order/check_delivery_distance/${lat}/${lng}/${marketplaceLat}/${marketplaceLng}`,
      { params: { deliveryType }, headers: bearerHeader(), signal }
    )
    .then((res) => res.data);
};

export interface DeliveryTimeSlotsParams {
  lat: number;
  lng: number;
  marketplaceLat: number;
  marketplaceLng: number;
  standardDeliveryDays?: number;
}

/**
 * GET check_delivery_time_slots -> DeliveryTimeSlotsResponse.
 * Uses a NON-intercepted axios so an expected VC-2 403 cannot log the user out.
 */
export const checkDeliveryTimeSlots = (
  params: DeliveryTimeSlotsParams,
  signal?: AbortSignal
): Promise<DeliveryTimeSlotsResponse> => {
  const {
    lat,
    lng,
    marketplaceLat,
    marketplaceLng,
    standardDeliveryDays = STANDARD_DELIVERY_DAYS,
  } = params;
  return rawAxios
    .get<DeliveryTimeSlotsResponse>(
      `${baseUrl}/click_and_collect_order/check_delivery_time_slots`,
      {
        params: {
          latitude: lat,
          longitude: lng,
          marketplaceLatitude: marketplaceLat,
          marketplaceLongitude: marketplaceLng,
          standardDeliveryDays,
        },
        // SR pin: the response's day keys are backend-localized via
        // Accept-Language (DANAS/SUTRA vs TODAY/TOMORROW); without the pin the
        // browser's own language preference decides what the Serbian UI shows.
        headers: { ...bearerHeader(), "Accept-Language": "SR" },
        signal,
      }
    )
    .then((res) => res.data);
};
